import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import {
  Button,
  Modal,
  ModalFooter,
  TextField,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOkapiKy } from '@folio/stripes/core';
import { useQueryClient } from '@tanstack/react-query';
import LocalizedTextField from '../../components/common/LocalizedTextField';

/**
 * Shared modal for editing a field label record.
 * Uses react-final-form + FieldArray + LocalizedTextField
 * to stay consistent with the localized field schema: [{locale, value}]
 *
 * Props:
 *  - fieldKey   : the Cube.js field key (read-only display)
 *  - displayName: human-readable name shown in the modal title
 *  - onClose    : called when modal is dismissed or saved
 */
function FieldLabelEditModal({ fieldKey, displayName, onClose }) {
  const intl        = useIntl();
  const okapiKy     = useOkapiKy();
  const queryClient = useQueryClient();

  const [existing, setExisting]         = React.useState(null);
  const [loadError, setLoadError]       = React.useState(null);
  const [initialValues, setInitialValues] = React.useState({
    label: [{ locale: 'en-US', value: displayName || '' }],
  });

  // Fetch existing record on mount — okapiKy excluded from deps (stable ref not guaranteed)
  useEffect(() => {
    if (!fieldKey) return;
    okapiKy
      .get(`field-labels?query=fieldKey%3D%3D%22${encodeURIComponent(fieldKey)}%22&limit=1`)
      .json()
      .then(res => {
        const record = res?.fieldLabels?.[0] || null;
        setExisting(record);
        if (record?.label?.length) {
          setInitialValues({ label: record.label });
        }
      })
      .catch(e => setLoadError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldKey]);

  const handleSubmit = async (values) => {
    const payload = {
      fieldKey,
      label: (values.label || []).filter(l => l.locale && l.value),
    };

    try {
      if (existing?.id) {
        await okapiKy.put(`field-labels/${existing.id}`, {
          json: { ...existing, ...payload },
        });
      } else {
        await okapiKy.post('field-labels', { json: payload });
      }
      queryClient.invalidateQueries(['field-labels']);
      onClose();
    } catch (e) {
      return { label: e.message || 'Save failed' };
    }
    return undefined;
  };

  return (
    <Form
      onSubmit={handleSubmit}
      mutators={{ ...arrayMutators }}
      initialValues={initialValues}
      render={({ handleSubmit: submit, submitting, submitError }) => (
        <Modal
          open
          size="medium"
          label={
            <FormattedMessage
              id="ui-reports.fieldLabels.editModal.title"
              defaultMessage="Edit label: {key}"
              values={{ key: displayName || fieldKey }}
            />
          }
          onClose={onClose}
          footer={
            <ModalFooter>
              <Button
                buttonStyle="primary"
                disabled={submitting}
                marginBottom0
                onClick={submit}
              >
                {submitting
                  ? <FormattedMessage id="ui-reports.fieldLabels.saving" defaultMessage="Saving..." />
                  : <FormattedMessage id="stripes-core.button.save" defaultMessage="Save" />}
              </Button>
              <Button
                marginBottom0
                disabled={submitting}
                onClick={onClose}
              >
                <FormattedMessage id="stripes-core.button.cancel" defaultMessage="Cancel" />
              </Button>
            </ModalFooter>
          }
        >
          <form onSubmit={submit}>
            {/* fieldKey — read only */}
            <TextField
              label={intl.formatMessage({ id: 'ui-reports.fieldLabels.fieldKey', defaultMessage: 'Field Key' })}
              value={fieldKey}
              readOnly
            />

            {loadError && (
              <div style={{ color: 'red', marginBottom: '0.5rem' }}>{loadError}</div>
            )}

            {/* Localized labels — uses existing LocalizedTextField component */}
            <FieldArray name="label">
              {({ fields }) => (
                <LocalizedTextField
                  fields={fields}
                  fieldLabel={intl.formatMessage({
                    id: 'ui-reports.fieldLabels.labelField',
                    defaultMessage: 'Label',
                  })}
                  isRequired
                />
              )}
            </FieldArray>

            {submitError && (
              <div style={{ color: 'red', marginTop: '0.5rem' }}>{submitError}</div>
            )}
          </form>
        </Modal>
      )}
    />
  );
}

FieldLabelEditModal.propTypes = {
  fieldKey:    PropTypes.string.isRequired,
  displayName: PropTypes.string,
  onClose:     PropTypes.func.isRequired,
};

export default FieldLabelEditModal;
